import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Pencil, Trash2, Plus, LogOut, Clock, ImageOff, Volume2, VolumeX, BellRing, Info, PowerOff, Power, Rocket, FileText, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import DateRangeFilter from "@/components/DateRangeFilter";
import OrderDetailDialog from "@/components/OrderDetailDialog";

const DAYS = [
  ["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"],
  ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"],
];
const STATUSES = ["received", "confirmed", "preparing", "collecting", "on-the-way", "delivered"];

const emptyItem = (rid) => ({
  restaurant_id: rid, category: "Pizza", name: "", description: "",
  price: 0, image: "", is_popular: false, is_family_deal: false, is_active: true,
  addons: [], removable_ingredients: [],
});

const CATEGORY_SUGGESTIONS = ["Pizza", "Starters", "Drinks", "Desserts", "Popular", "Family Meals", "Main Courses"];

const RestaurantAdmin = () => {
  const { admin, logout } = useAuth();
  const nav = useNavigate();
  const rid = admin?.restaurant_id;

  const [restaurant, setRestaurant] = useState(null);
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [riders, setRiders] = useState([]);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [detailOrder, setDetailOrder] = useState(null);
  const [summary, setSummary] = useState({ count: 0, revenue: 0 });
  const [editItem, setEditItem] = useState(null);
  const [savingR, setSavingR] = useState(false);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("ce_sound_on") === "1");
  const [newOrderPulse, setNewOrderPulse] = useState(false);

  const knownOrdersRef = useRef(null);
  const soundOnRef = useRef(soundOn);
  const audioCtxRef = useRef(null);
  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);

  const ensureAudioCtx = () => {
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) audioCtxRef.current = new AC();
    }
    return audioCtxRef.current;
  };

  const playChime = useCallback(() => {
    const ctx = ensureAudioCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const now = ctx.currentTime;
    // Two-tone chime: 880Hz then 1320Hz, twice
    [880, 1320, 880, 1320].forEach((freq, i) => {
      const start = now + i * 0.18;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.28, start + 0.02);
      gain.gain.linearRampToValueAtTime(0, start + 0.16);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.18);
    });
  }, []);

  const toggleSound = () => {
    const next = !soundOn;
    localStorage.setItem("ce_sound_on", next ? "1" : "0");
    setSoundOn(next);
    if (next) {
      const ctx = ensureAudioCtx();
      if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
      playChime();
      toast.success("Sound alerts enabled");
    } else {
      toast.message("Sound alerts muted");
    }
  };

  const load = async () => {
    if (!rid) return;
    const params = { params: dateRange };
    const [r, m, o, s, rd] = await Promise.all([
      api.get(`/restaurants/${rid}`),
      api.get(`/restaurants/${rid}/menu`, { params: { include_inactive: true } }),
      api.get("/orders", params),
      api.get("/orders-summary/today", params),
      api.get("/riders").catch(() => ({ data: [] })),
    ]);
    setRestaurant(r.data); setItems(m.data); setOrders(o.data); setSummary(s.data); setRiders(rd.data);

    const numbers = new Set((o.data || []).map(x => x.order_number));
    if (knownOrdersRef.current === null) {
      knownOrdersRef.current = numbers;
    } else {
      const newOnes = [...numbers].filter(n => !knownOrdersRef.current.has(n));
      if (newOnes.length > 0) {
        knownOrdersRef.current = numbers;
        setNewOrderPulse(true);
        setTimeout(() => setNewOrderPulse(false), 6000);
        toast.success(`${newOnes.length} new order${newOnes.length > 1 ? "s" : ""}! (${newOnes.join(", ")})`, { duration: 8000 });
        if (soundOnRef.current) playChime();
      }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rid, dateRange.start, dateRange.end]);
  useEffect(() => {
    if (!rid) return;
    const iv = setInterval(() => load(), 12000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [rid, dateRange.start, dateRange.end]);

  const saveRestaurant = async () => {
    setSavingR(true);
    try {
      await api.put(`/restaurants/${rid}`, restaurant);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); } finally { setSavingR(false); }
  };

  const toggleTemporarilyClosed = async (closed) => {
    try {
      const next = { ...restaurant, is_temporarily_closed: closed };
      await api.put(`/restaurants/${rid}`, next);
      setRestaurant(next);
      toast.success(closed ? "Negozio chiuso provvisoriamente" : "Negozio riaperto — visibile sull'app");
    } catch {
      toast.error("Operation failed");
    }
  };

  const saveItem = async () => {
    if (!editItem.name || !editItem.price) { toast.error("Name and price required"); return; }
    try {
      if (editItem.id) await api.put(`/menu-items/${editItem.id}`, editItem);
      else await api.post("/menu-items", editItem);
      toast.success("Saved");
      setEditItem(null);
      const m = await api.get(`/restaurants/${rid}/menu`, { params: { include_inactive: true } });
      setItems(m.data);
    } catch { toast.error("Save failed"); }
  };

  const delItem = async (id) => {
    if (!confirm("Delete this dish?")) return;
    await api.delete(`/menu-items/${id}`);
    toast.success("Deleted");
    setItems((prev) => prev.filter(i => i.id !== id));
  };

  const toggleActive = async (id) => {
    const r = await api.patch(`/menu-items/${id}/toggle-active`);
    setItems((prev) => prev.map(i => i.id === id ? { ...i, is_active: r.data.is_active } : i));
  };

  const setOrderStatus = async (num, status) => {
    await api.patch(`/orders/${num}/status`, { status });
    toast.success("Order updated");
    load();
  };

  const assignRider = async (num, riderId) => {
    await api.patch(`/orders/${num}/assign-rider`, { rider_id: riderId || null });
    toast.success(riderId ? "Rider assigned" : "Rider cleared");
    load();
  };

  if (!admin) return null;
  if (!rid) {
    return (
      <div className="max-w-lg mx-auto py-20 text-center">
        <p className="text-navy/70">No restaurant is linked to your account. Contact the platform administrator.</p>
      </div>
    );
  }
  if (!restaurant) return <div className="min-h-[50vh] flex items-center justify-center text-navy/50">Loading…</div>;

  const setR = (k, v) => setRestaurant((prev) => ({ ...prev, [k]: v }));
  const setHour = (day, kind, val) => setRestaurant((prev) => ({
    ...prev,
    opening_hours: { ...prev.opening_hours, [day]: { ...(prev.opening_hours?.[day] || {}), [kind]: val } },
  }));

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-teal font-bold">Restaurant Panel</div>
          <h1 className="font-heading text-3xl font-extrabold text-navy tracking-tight mt-1">{restaurant.name}</h1>
          <p className="text-navy/60 text-sm mt-1">Signed in as <span className="font-semibold">{admin.email}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={toggleSound}
            className={`rounded-full border-2 ${soundOn ? "bg-teal text-white border-teal hover:bg-teal/90 hover:text-white" : "border-navy text-navy hover:bg-navy hover:text-white"} ${newOrderPulse ? "animate-pulse ring-4 ring-coral/40" : ""}`}
            data-testid="sound-toggle-btn"
            title={soundOn ? "Mute new-order sound" : "Enable new-order sound"}
          >
            {soundOn ? <Volume2 className="w-4 h-4 mr-1" /> : <VolumeX className="w-4 h-4 mr-1" />}
            {soundOn ? "Sound on" : "Sound off"}
          </Button>
          <Button
            variant="outline"
            onClick={() => { logout(); nav("/restaurant-admin/login"); }}
            className="rounded-full border-2 border-navy text-navy hover:bg-navy hover:text-white"
            data-testid="restaurant-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-1" /> Log out
          </Button>
        </div>
      </div>

      <div className="mt-4">
        <DateRangeFilter
          start={dateRange.start}
          end={dateRange.end}
          onChange={setDateRange}
          testIdPrefix="restaurant-range"
          label="Filtro periodo (ordini + commissioni)"
        />
      </div>

      {newOrderPulse && (
        <div className="mt-4 rounded-2xl border-2 border-coral bg-coral/10 p-3 flex items-center gap-3 animate-fade-up" data-testid="new-order-banner">
          <BellRing className="w-5 h-5 text-coral animate-pulse" />
          <span className="font-heading font-bold text-navy">New order just arrived — check the Orders tab.</span>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        <div className="bg-white p-5 rounded-2xl border border-navy/10">
          <div className="text-xs uppercase text-navy/50">Orders {(dateRange.start || dateRange.end) ? "(nel periodo)" : "today"}</div>
          <div className="font-heading text-3xl font-extrabold text-navy mt-1">{summary.count}</div>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-navy/10">
          <div className="text-xs uppercase text-navy/50">Revenue {(dateRange.start || dateRange.end) ? "(nel periodo)" : "today"}</div>
          <div className="font-heading text-3xl font-extrabold text-coral mt-1">€{summary.revenue.toFixed(2)}</div>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-navy/10">
          <div className="text-xs uppercase text-navy/50">Menu items</div>
          <div className="font-heading text-3xl font-extrabold text-navy mt-1">{items.length}</div>
        </div>
      </div>

      <Tabs defaultValue="menu" className="mt-6">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="menu" data-testid="tab-r-menu">Menu</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-r-orders">Orders</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-r-settings">Settings & hours</TabsTrigger>
        </TabsList>

        {/* MENU */}
        <TabsContent value="menu">
          <div className="flex justify-between items-center mb-4">
            <p className="text-navy/60 text-sm">Add, edit and toggle dishes on/off. Disabled dishes are hidden from customers.</p>
            <Button onClick={() => setEditItem(emptyItem(rid))} className="btn-coral" data-testid="add-dish-btn">
              <Plus className="w-4 h-4 mr-1" /> Add dish
            </Button>
          </div>
          <div className="grid gap-2">
            {items.map(it => (
              <div key={it.id} className={`bg-white p-3 rounded-2xl border border-navy/10 flex gap-3 items-center ${it.is_active === false ? "opacity-60" : ""}`} data-testid={`dish-${it.id}`}>
                {it.image ? (
                  <img src={it.image} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-sand-100 flex items-center justify-center shrink-0"><ImageOff className="w-5 h-5 text-navy/30" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-bold text-navy">{it.name}</span>
                    <span className="text-[10px] uppercase font-bold bg-sand-100 px-1.5 py-0.5 rounded">{it.category}</span>
                    {it.is_active === false && <span className="text-[10px] uppercase font-bold text-white bg-navy/70 px-1.5 py-0.5 rounded">Hidden</span>}
                  </div>
                  {it.description && <div className="text-xs text-navy/60 line-clamp-1 mt-0.5">{it.description}</div>}
                  <div className="text-sm text-navy/70 mt-1 font-semibold">€{Number(it.price).toFixed(2)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <Switch checked={it.is_active !== false} onCheckedChange={() => toggleActive(it.id)} data-testid={`toggle-${it.id}`} />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setEditItem(it)} data-testid={`edit-${it.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => delItem(it.id)} data-testid={`del-${it.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
            {items.length === 0 && <p className="text-center text-navy/50 py-10">No dishes yet — add your first dish above.</p>}
          </div>
        </TabsContent>

        {/* ORDERS */}
        <TabsContent value="orders">
          <div className="bg-white rounded-2xl border border-navy/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-navy/50 bg-sand-50">
                <tr>
                  <th className="p-3">#</th>
                  <th>Data/ora</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Delivery to</th>
                  <th>Rider</th>
                  <th className="text-right">Products</th>
                  <th className="text-right">Commission (20%)</th>
                  <th className="text-right">Net to you</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => {
                  // Backend now stores commission_amount + restaurant_net, but fall back
                  // to on-the-fly calc for orders placed before this feature shipped.
                  const productsAmt = typeof o.subtotal === "number" ? o.subtotal : 0;
                  const commission = typeof o.commission_amount === "number"
                    ? o.commission_amount
                    : Number((productsAmt * 0.20).toFixed(2));
                  const net = typeof o.restaurant_net === "number"
                    ? o.restaurant_net
                    : Number((productsAmt - commission - (o.discount || 0)).toFixed(2));
                  return (
                    <tr
                      key={o.order_number}
                      className="border-t border-navy/5 cursor-pointer hover:bg-sand-50 transition"
                      onClick={() => setDetailOrder(o)}
                      data-testid={`order-row-${o.order_number}`}
                    >
                      <td className="p-3 font-mono text-xs">{o.order_number}</td>
                      <td className="text-xs text-navy/70 whitespace-nowrap" data-testid={`order-date-${o.order_number}`}>{new Date(o.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                      <td>{o.customer.first_name} {o.customer.last_name}<div className="text-[11px] text-navy/50">{o.customer.phone}</div></td>
                      <td className="text-xs">{o.items.map(i => `${i.qty}× ${i.name}`).join(", ")}</td>
                      <td className="text-xs text-navy/70">{o.delivery.accommodation_type} {o.delivery.room && `— ${o.delivery.room}`}<div className="text-[11px] text-navy/50">{o.delivery.address}</div></td>
                      <td onClick={e => e.stopPropagation()}>
                        <Select value={o.assigned_rider_id || "none"} onValueChange={v => assignRider(o.order_number, v === "none" ? null : v)}>
                          <SelectTrigger className="h-8 w-40" data-testid={`assign-rider-${o.order_number}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— Unassigned —</SelectItem>
                            {riders.map(r => <SelectItem key={r.id} value={r.id}>{r.name || r.email}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="text-right font-semibold" data-testid={`order-products-${o.order_number}`}>€{productsAmt.toFixed(2)}</td>
                      <td className="text-right text-coral" data-testid={`order-commission-${o.order_number}`}>-€{commission.toFixed(2)}</td>
                      <td className="text-right font-bold text-teal" data-testid={`order-net-${o.order_number}`}>€{net.toFixed(2)}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <Select value={o.status} onValueChange={v => setOrderStatus(o.order_number, v)}>
                          <SelectTrigger className="h-8 w-40" data-testid={`order-status-${o.order_number}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
                {orders.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-navy/40">No orders yet</td></tr>}
              </tbody>
              {orders.length > 0 && (() => {
                const totProducts = orders.reduce((s, o) => s + (o.subtotal || 0), 0);
                const totCommission = orders.reduce((s, o) => s + (typeof o.commission_amount === "number" ? o.commission_amount : (o.subtotal || 0) * 0.20), 0);
                const totNet = orders.reduce((s, o) => {
                  const c = typeof o.commission_amount === "number" ? o.commission_amount : (o.subtotal || 0) * 0.20;
                  return s + (typeof o.restaurant_net === "number" ? o.restaurant_net : (o.subtotal || 0) - c - (o.discount || 0));
                }, 0);
                return (
                  <tfoot className="bg-sand-50 border-t-2 border-navy/10 font-bold" data-testid="orders-totals">
                    <tr>
                      <td className="p-3" colSpan={5}>Totals ({orders.length} order{orders.length !== 1 ? "s" : ""})</td>
                      <td className="text-right">€{totProducts.toFixed(2)}</td>
                      <td className="text-right text-coral">-€{totCommission.toFixed(2)}</td>
                      <td className="text-right text-teal">€{totNet.toFixed(2)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                );
              })()}
            </table>
            <p className="text-[11px] text-navy/40 px-4 py-2 border-t border-navy/5">
              Delivery is handled and priced by the platform — it never appears in your restaurant accounting.
            </p>
          </div>
        </TabsContent>

        {/* SETTINGS */}
        <TabsContent value="settings" className="space-y-6">
          <div className="bg-white p-6 rounded-2xl border border-navy/10">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-heading font-bold text-navy flex items-center gap-2">
                Delivery & basic info
              </h3>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="inline-flex items-center gap-1 text-xs font-semibold text-coral hover:bg-coral/10 rounded-full px-3 py-1.5 border border-coral/30" data-testid="in-primo-piano-info">
                    <Rocket className="w-3.5 h-3.5" /> In Primo Piano <Info className="w-3 h-3" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-96 max-h-[70vh] overflow-y-auto text-sm leading-relaxed" side="left" align="start">
                  <div className="space-y-2">
                    <p className="font-heading font-bold text-navy text-base">🚀 Vuoi il massimo dei clienti?</p>
                    <p className="text-navy/80">Diventa il numero uno a Caleta de Fuste. Non accontentarti di esserci: fai in modo che tutti ti vedano <b>per primi</b>. Quando un cliente apre l'app affamato e pronto a ordinare, lo spazio in cima alla schermata è il più prezioso dell'isola.</p>
                    <p className="font-heading font-bold text-navy pt-1">Come funziona il servizio In Primo Piano:</p>
                    <ul className="list-none pl-0 space-y-1 text-navy/80 text-xs">
                      <li><b>Posizione esclusiva</b> — il tuo ristorante compare in cima alla lista, prima di tutti i concorrenti.</li>
                      <li><b>Massima visibilità</b> — catturi turisti appena arrivati negli hotel e residenti.</li>
                      <li><b>Più ordini, zero sforzo</b> — più visualizzazioni = più scontrini staccati ogni giorno.</li>
                    </ul>
                    <div className="bg-coral/10 border border-coral/30 rounded-xl p-3 mt-2">
                      <p className="font-heading font-bold text-coral">Quanto costa? Solo 5,00 € al giorno.</p>
                      <p className="text-xs text-navy/70 mt-1">Ti basta un solo ordine in più al giorno per ripagare la sponsorizzazione — tutto il resto è guadagno puro. Nessun vincolo: scegli tu quanti giorni attivarlo. Attivazione immediata con pagamento su carta o link WhatsApp.</p>
                    </div>
                    <a
                      href="https://wa.me/34000000000?text=Ciao%20Caleta%20Eats%2C%20voglio%20attivare%20In%20Primo%20Piano"
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 flex items-center justify-center gap-2 bg-teal hover:bg-teal/90 text-white font-bold rounded-full py-2.5 text-sm"
                      data-testid="whatsapp-featured-cta"
                    >
                      <MessageCircle className="w-4 h-4" /> Attiva su WhatsApp
                    </a>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="flex items-center gap-1 text-navy/60">
                  Delivery fee (€) <Info className="w-3 h-3" />
                </Label>
                <Input
                  type="number"
                  step="0.5"
                  value={restaurant.delivery_fee}
                  readOnly
                  disabled
                  className="bg-sand-50 text-navy/60 cursor-not-allowed"
                  data-testid="r-delivery-fee"
                />
                <p className="text-[11px] text-navy/50 mt-1">Managed centrally by the platform (€2.99 over €20, otherwise €4.99).</p>
              </div>
              <div><Label>Minimum order (€)</Label><Input type="number" step="0.5" value={restaurant.min_order} onChange={e => setR("min_order", Number(e.target.value))} data-testid="r-min-order" /></div>
              <div><Label>Estimated delivery time</Label><Input value={restaurant.delivery_time} onChange={e => setR("delivery_time", e.target.value)} placeholder="30-45 min" /></div>
              <div><Label>Phone</Label><Input value={restaurant.phone || ""} onChange={e => setR("phone", e.target.value)} /></div>
              <div><Label>Restaurant email (for order tickets)</Label><Input type="email" value={restaurant.email || ""} onChange={e => setR("email", e.target.value)} data-testid="r-email" /></div>
              <div><Label>Address</Label><Input value={restaurant.address || ""} onChange={e => setR("address", e.target.value)} /></div>
              <div className="sm:col-span-2"><Label>Description</Label><Textarea value={restaurant.description || ""} onChange={e => setR("description", e.target.value)} /></div>
            </div>
          </div>

          {/* FISCAL DATA */}
          <div className="bg-white p-6 rounded-2xl border border-navy/10">
            <h3 className="font-heading font-bold text-navy mb-1 flex items-center gap-2">
              <FileText className="w-4 h-4" /> Dati fiscali
            </h3>
            <p className="text-xs text-navy/50 mb-4">Only you can see and edit these fields — used for invoicing and monthly settlements.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Ragione sociale</Label><Input value={restaurant.fiscal_data?.ragione_sociale || ""} onChange={e => setR("fiscal_data", { ...(restaurant.fiscal_data || {}), ragione_sociale: e.target.value })} data-testid="r-fiscal-ragione" /></div>
              <div><Label>NIF / CIF</Label><Input value={restaurant.fiscal_data?.nif || ""} onChange={e => setR("fiscal_data", { ...(restaurant.fiscal_data || {}), nif: e.target.value })} data-testid="r-fiscal-nif" /></div>
              <div className="sm:col-span-2"><Label>Indirizzo fiscale</Label><Input value={restaurant.fiscal_data?.indirizzo || ""} onChange={e => setR("fiscal_data", { ...(restaurant.fiscal_data || {}), indirizzo: e.target.value })} data-testid="r-fiscal-indirizzo" /></div>
              <div><Label>IBAN</Label><Input value={restaurant.fiscal_data?.iban || ""} onChange={e => setR("fiscal_data", { ...(restaurant.fiscal_data || {}), iban: e.target.value })} data-testid="r-fiscal-iban" /></div>
              <div><Label>Nome banca</Label><Input value={restaurant.fiscal_data?.nome_banca || ""} onChange={e => setR("fiscal_data", { ...(restaurant.fiscal_data || {}), nome_banca: e.target.value })} data-testid="r-fiscal-banca" /></div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-navy/10">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className="font-heading font-bold text-navy flex items-center gap-2"><Clock className="w-4 h-4" /> Opening hours</h3>

              {restaurant.is_temporarily_closed ? (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-full border-teal text-teal hover:bg-teal hover:text-white" data-testid="reopen-shop-btn">
                      <Power className="w-3.5 h-3.5 mr-1" /> Reopen shop
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Reopen your restaurant?</AlertDialogTitle>
                      <AlertDialogDescription>Your restaurant will appear again in the app and customers will be able to place orders.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => toggleTemporarilyClosed(false)} data-testid="confirm-reopen-btn">Yes, reopen</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              ) : (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-full border-coral text-coral hover:bg-coral hover:text-white" data-testid="close-shop-btn">
                      <PowerOff className="w-3.5 h-3.5 mr-1" /> Chiudi negozio provvisoriamente
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Chiudere il negozio provvisoriamente?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Il tuo ristorante sarà <b>invisibile</b> ai clienti sull'app finché non lo riattivi da questo stesso pulsante. Gli ordini già ricevuti non vengono cancellati.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annulla</AlertDialogCancel>
                      <AlertDialogAction onClick={() => toggleTemporarilyClosed(true)} className="bg-coral hover:bg-coral/90" data-testid="confirm-close-btn">Sì, chiudi ora</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
            {restaurant.is_temporarily_closed && (
              <div className="mb-3 p-3 rounded-xl bg-coral/10 border border-coral/30 text-sm text-coral font-semibold flex items-center gap-2" data-testid="closed-banner">
                <PowerOff className="w-4 h-4" /> Il tuo ristorante è temporaneamente chiuso e non compare nell'app.
              </div>
            )}
            <p className="text-xs text-navy/50 mb-3">Use 24-hour format (e.g. 12:00 – 23:00). Leave both fields empty to mark that day as closed.</p>
            <div className="space-y-2">
              {DAYS.map(([key, label]) => (
                <div key={key} className="grid grid-cols-12 items-center gap-2">
                  <span className="col-span-4 sm:col-span-3 text-sm font-medium">{label}</span>
                  <Input className="col-span-4 sm:col-span-4 h-9" placeholder="12:00" value={restaurant.opening_hours?.[key]?.open || ""} onChange={e => setHour(key, "open", e.target.value)} data-testid={`hours-${key}-open`} />
                  <span className="col-span-1 text-center text-navy/40">–</span>
                  <Input className="col-span-3 sm:col-span-4 h-9" placeholder="23:00" value={restaurant.opening_hours?.[key]?.close || ""} onChange={e => setHour(key, "close", e.target.value)} data-testid={`hours-${key}-close`} />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={saveRestaurant} disabled={savingR} className="btn-coral" data-testid="save-settings-btn">
              {savingR ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Item edit dialog */}
      <Dialog open={!!editItem} onOpenChange={(v) => !v && setEditItem(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editItem?.id ? "Edit dish" : "Add dish"}</DialogTitle></DialogHeader>
          {editItem && (
            <div className="grid gap-3">
              <div><Label>Dish name*</Label><Input value={editItem.name} onChange={e => setEditItem({ ...editItem, name: e.target.value })} data-testid="dish-name" /></div>
              <div><Label>Category*</Label>
                {(() => {
                  const isCustom = editItem.category && !CATEGORY_SUGGESTIONS.includes(editItem.category);
                  const selectValue = isCustom ? "__custom__" : (editItem.category || "");
                  return (
                    <>
                      <Select
                        value={selectValue}
                        onValueChange={v => setEditItem({ ...editItem, category: v === "__custom__" ? "" : v })}
                      >
                        <SelectTrigger data-testid="dish-category"><SelectValue placeholder="Select a category" /></SelectTrigger>
                        <SelectContent>
                          {CATEGORY_SUGGESTIONS.map(c => (
                            <SelectItem key={c} value={c} data-testid={`dish-category-option-${c.toLowerCase().replace(/\s+/g, '-')}`}>{c}</SelectItem>
                          ))}
                          <SelectItem value="__custom__" data-testid="dish-category-option-custom">Custom…</SelectItem>
                        </SelectContent>
                      </Select>
                      {(isCustom || selectValue === "__custom__") && (
                        <Input
                          className="mt-2"
                          value={editItem.category}
                          onChange={e => setEditItem({ ...editItem, category: e.target.value })}
                          placeholder="Type a custom category"
                          data-testid="dish-category-custom-input"
                        />
                      )}
                      <p className="text-[11px] text-navy/50 mt-1">Pizza, Starters (Antipasti), Drinks (Bibite), Desserts (Dolci), Popular</p>
                    </>
                  );
                })()}
              </div>
              <div><Label>Price (€)*</Label><Input type="number" step="0.1" value={editItem.price} onChange={e => setEditItem({ ...editItem, price: Number(e.target.value) })} data-testid="dish-price" /></div>
              <div><Label>Description</Label><Textarea value={editItem.description} onChange={e => setEditItem({ ...editItem, description: e.target.value })} placeholder="Short description of the dish" data-testid="dish-description" /></div>
              <div><Label>Photo URL</Label>
                <Input value={editItem.image} onChange={e => setEditItem({ ...editItem, image: e.target.value })} placeholder="https://…" data-testid="dish-image" />
                {editItem.image && <img src={editItem.image} alt="" className="mt-2 w-full h-32 object-cover rounded-lg" />}
              </div>

              {/* REMOVABLE INGREDIENTS EDITOR */}
              <div className="border-t border-navy/10 pt-3">
                <Label className="flex items-center justify-between">
                  <span>Removable ingredients</span>
                  <span className="text-[11px] font-normal text-navy/50">Customers can tick to exclude</span>
                </Label>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(editItem.removable_ingredients || []).map((ing, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-sand-100 text-navy text-xs rounded-full pl-3 pr-1 py-1" data-testid={`ing-chip-${i}`}>
                      {ing}
                      <button
                        type="button"
                        onClick={() => setEditItem({ ...editItem, removable_ingredients: editItem.removable_ingredients.filter((_, idx) => idx !== i) })}
                        className="w-5 h-5 rounded-full bg-white hover:bg-coral hover:text-white flex items-center justify-center"
                      >×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="e.g. Mushrooms"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && e.target.value.trim()) {
                        e.preventDefault();
                        setEditItem({ ...editItem, removable_ingredients: [...(editItem.removable_ingredients || []), e.target.value.trim()] });
                        e.target.value = "";
                      }
                    }}
                    data-testid="ing-input"
                  />
                  <span className="text-[11px] text-navy/40 self-center whitespace-nowrap">Enter to add</span>
                </div>
              </div>

              {/* ADD-ONS EDITOR */}
              <div className="border-t border-navy/10 pt-3">
                <Label className="flex items-center justify-between">
                  <span>Extra add-ons (with price)</span>
                  <span className="text-[11px] font-normal text-navy/50">Extra income per order</span>
                </Label>
                <div className="space-y-1.5 mt-2">
                  {(editItem.addons || []).map((a, i) => (
                    <div key={i} className="flex gap-2 items-center" data-testid={`addon-row-${i}`}>
                      <Input
                        value={a.name}
                        onChange={e => {
                          const next = [...editItem.addons]; next[i] = { ...next[i], name: e.target.value };
                          setEditItem({ ...editItem, addons: next });
                        }}
                        placeholder="Add-on name"
                        className="flex-1"
                      />
                      <Input
                        type="number" step="0.1"
                        value={a.price}
                        onChange={e => {
                          const next = [...editItem.addons]; next[i] = { ...next[i], price: Number(e.target.value) };
                          setEditItem({ ...editItem, addons: next });
                        }}
                        placeholder="€"
                        className="w-24"
                      />
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => setEditItem({ ...editItem, addons: editItem.addons.filter((_, idx) => idx !== i) })}
                      ><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                </div>
                <Button
                  type="button" variant="outline" size="sm" className="mt-2"
                  onClick={() => setEditItem({ ...editItem, addons: [...(editItem.addons || []), { name: "", price: 1.0 }] })}
                  data-testid="add-addon-btn"
                ><Plus className="w-3.5 h-3.5 mr-1" /> Add add-on</Button>
              </div>

              <div className="flex gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editItem.is_popular} onChange={e => setEditItem({ ...editItem, is_popular: e.target.checked })} /> Popular</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editItem.is_family_deal} onChange={e => setEditItem({ ...editItem, is_family_deal: e.target.checked })} /> Family deal</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editItem.is_active !== false} onChange={e => setEditItem({ ...editItem, is_active: e.target.checked })} /> Visible to customers</label>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setEditItem(null)}>Cancel</Button>
                <Button onClick={saveItem} className="btn-coral" data-testid="save-dish-btn">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <OrderDetailDialog
        order={detailOrder}
        open={!!detailOrder}
        onOpenChange={(v) => !v && setDetailOrder(null)}
      />
    </div>
  );
};

export default RestaurantAdmin;
